import { useCallback, useEffect, useMemo, useRef } from 'react';
import { getFieldAccessPermissions } from 'components/Fields/FieldInput/helpers';
import { isEmailStatusImmutable } from 'components/Fields/helpers';
import { KEY_CODES } from './constants';
import { getEventKey } from './useCustomSelectMenuProps';
import { isMobile } from 'react-device-detect';
import { isComposingKeyboardEvent } from 'utility/keyboard';

const nextFocus = (e, focusables, focused, handles, limit = 50) => {
  const nextFocusable = e.shiftKey
    ? focusables[focusables.indexOf(focused.current) - 1] || focusables.at(-1)
    : focusables[focusables.indexOf(focused.current) + 1] || focusables[0];
  focused.current = nextFocusable;

  if (
    nextFocusable &&
    (!handles.current[nextFocusable] ||
      handles.current[nextFocusable]?.disabled) &&
    limit
  ) {
    nextFocus(e, focusables, focused, handles, limit - 1);
  }

  handles.current[nextFocusable]?.customFocus?.(e);
};

const handleKeyEvent = (e, focusables, focused, handles) => {
  if (isComposingKeyboardEvent(e)) {
    return;
  }
  const handle = handles.current[focused.current];

  switch (getEventKey(e)) {
    case KEY_CODES.tab.type: {
      e.preventDefault();
      if (handle) {
        const shouldStayFocused = handle.customTab?.(e);
        !shouldStayFocused && nextFocus(e, focusables, focused, handles);
      }
      return;
    }
    case KEY_CODES.enter.type: {
      if (handle) {
        const shouldStayFocused = handle.customEnter?.(e);
        !shouldStayFocused && nextFocus(e, focusables, focused, handles);
      }
      return;
    }
    case KEY_CODES.arrowUp.type: {
      if (handle) {
        handle.customUp?.(e);
      }
      return;
    }
    case KEY_CODES.arrowDown.type: {
      if (handle) {
        handle.customDown?.(e);
      }
      return;
    }
    case KEY_CODES.arrowLeft.type: {
      if (handle) {
        handle.customLeft?.(e);
      }
      return;
    }
    case KEY_CODES.arrowRight.type: {
      if (handle) {
        handle.customRight?.(e);
      }
      return;
    }
    case KEY_CODES.esc.type: {
      if (handle) {
        handle.customEscape?.(e);
      }
      return;
    }
    case KEY_CODES.space.type: {
      if (handle) {
        handle.customSpace?.(e);
      }
      return;
    }
    case KEY_CODES.letter.type:
    case KEY_CODES.digit.type:
    case KEY_CODES.punctuation.type: {
      if (handle) {
        handle.customInput?.(e);
      }
      return;
    }
    default: {
      return;
    }
  }
};

const getCanFocusField = (model) => {
  return (field) => {
    const [canEdit, canView] = getFieldAccessPermissions(field, model);

    return canEdit && canView && !isEmailStatusImmutable(model, field);
  };
};

export const useCanFocusField = (model) => {
  return useMemo(() => {
    return getCanFocusField(model);
  }, [model]);
};

/**
 * @typedef { id: string } FieldLike
 *
 * @param {FieldLike[]} fields
 * @param {{
 *  canFocusField?: (field: FieldLike) => boolean;
 *  suppressAutoScroll?: boolean;
 * }} options
 */
export const useKeyListeners = (fields, options = {}) => {
  const { canFocusField, suppressAutoScroll = false } = options;

  const focusables = useRef([]);
  const fieldHandles = useRef({});
  //possible values: undefined ( initial ), null, field-id
  const focused = useRef(undefined);
  const prevFocused = useRef();
  const onFocus = useCallback(
    (id) => () => {
      focused.current = id;
    },
    []
  );
  const onBlur = useCallback((e, shouldFocusNext) => {
    if (shouldFocusNext)
      nextFocus(e, focusables.current, focused, fieldHandles);
    else focused.current = null;
  }, []);

  const assignFieldHandle = useCallback((id, el) => {
    if (id !== undefined && !isMobile) fieldHandles.current[id] = el;
  }, []);

  useEffect(() => {
    const handler = (e) =>
      focused.current &&
      handleKeyEvent(e, focusables.current, focused, fieldHandles);
    document.addEventListener('keydown', handler, false);

    return () => document.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    const filtered = canFocusField ? fields.filter(canFocusField) : fields;
    focusables.current = filtered.map(({ id }) => id);
  }, [fields, canFocusField]);

  useEffect(() => {
    // prevent any custom handler for mobile devices
    if (!isMobile && !suppressAutoScroll) {
      if (focused.current === undefined) {
        focused.current = fields[0]?.id;
        if (
          !fieldHandles?.current[focused?.current] ||
          fieldHandles?.current[focused?.current]?.disabled
        ) {
          nextFocus(
            { shiftKey: false },
            focusables.current,
            focused,
            fieldHandles
          );
        }
      }
      if (focused.current && focused.current !== prevFocused.current) {
        fieldHandles?.current[focused?.current]?.customFocus?.();
      }
      prevFocused.current = focused.current;
    }
  });

  /**
   *
   * @type {function(*): {onBlur: function(*, *): void, shouldUseKeyboardEventHandle: boolean, onFocus: function(): void, fieldId: *}}
   */
  const getKeyListenersProps = useCallback(
    (id) => {
      return {
        fieldId: id,
        shouldUseKeyboardEventHandle: true,
        onFocus: onFocus(id),
        onBlur: onBlur,
      };
    },
    [onBlur, onFocus]
  );

  const resetFocus = useCallback(() => {
    focused.current = undefined;
  }, []);

  return {
    onFocus,
    onBlur,
    assignFieldHandle,
    getKeyListenersProps,
    resetFocus,
  };
};
